import React, { useEffect, useState } from "react";
import { ArrowRight } from "@phosphor-icons/react";

/**
 * Mobile-only sticky bottom call-to-action for donor/event pages.
 * The primary give/ticket form sits far down the page on phones, so this
 * bar floats a persistent CTA that scrolls to (and focuses) the form.
 * It auto-hides whenever the target form is on screen so it never covers
 * the real submit button. Desktop is unaffected (md:hidden).
 */
export const StickyGiveBar = ({
    targetId,
    ctaLabel,
    contextLabel,
    contextValue,
    testid = "sticky-give-bar",
}) => {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const target = document.getElementById(targetId);
        if (!target) return;
        const obs = new IntersectionObserver(
            ([entry]) => setVisible(!entry.isIntersecting),
            { threshold: 0, rootMargin: "0px 0px -35% 0px" }
        );
        obs.observe(target);
        return () => obs.disconnect();
    }, [targetId]);

    const go = () => {
        const target = document.getElementById(targetId);
        if (!target) return;
        target.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div
            className={`md:hidden fixed inset-x-0 bottom-0 z-40 transition-transform duration-300 ease-out ${visible ? "translate-y-0" : "translate-y-full"}`}
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            aria-hidden={!visible}
            data-testid={testid}
        >
            <div className="bg-card/95 backdrop-blur-md border-t border-border shadow-[0_-4px_24px_rgba(0,0,0,0.10)] px-4 py-3 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                    {contextLabel && <p className="label-eyebrow text-[10px] text-accent truncate">{contextLabel}</p>}
                    {contextValue && <p className="data-num text-base leading-tight truncate">{contextValue}</p>}
                </div>
                <button onClick={go} className="btn-accent shrink-0 px-6 py-3" data-testid={`${testid}-cta`}>
                    {ctaLabel} <ArrowRight size={16} weight="bold" />
                </button>
            </div>
        </div>
    );
};

export default StickyGiveBar;
